import { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Renders Nico photos/videos from the admin-managed Reviews Media library.
// Renders nothing when there are no visible items — no placeholders, no stock photos.
export default function NicoMedia({ reviewKey = "nico" }) {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get(`/review-media?review_key=${reviewKey}`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [reviewKey]);

  if (items.length === 0) return null;
  const alt = (m) => (lang === "es" ? m.alt_es : m.alt_en) || "";
  const caption = (m) => (lang === "es" ? m.caption_es : m.caption_en) || "";

  return (
    <div className="mt-10 space-y-6" data-testid="nico-media">
      <div className="grid gap-4 sm:grid-cols-2">
        {items.map((m) => (
          <figure key={m.id} className="space-y-2">
            {m.kind === "photo" ? (
              <button
                type="button"
                onClick={() => setActive(m)}
                data-testid={`nico-photo-${m.id}`}
                className="block w-full overflow-hidden rounded-2xl border border-sand-300 focus:outline-none focus:ring-2 focus:ring-terracotta"
              >
                <img src={`${BACKEND}${m.url}`} alt={alt(m)} loading="lazy" className="h-64 w-full object-cover transition-transform duration-300 hover:scale-[1.02]" />
              </button>
            ) : (
              <video
                data-testid={`nico-video-${m.id}`}
                controls
                muted
                playsInline
                preload="none"
                className="w-full rounded-2xl border border-sand-300"
              >
                <source src={`${BACKEND}${m.url}`} type={m.content_type || "video/mp4"} />
                {m.captions_en && <track kind="captions" srcLang="en" label="English" src={`${BACKEND}${m.captions_en}`} default={lang !== "es"} />}
                {m.captions_es && <track kind="captions" srcLang="es" label="Español" src={`${BACKEND}${m.captions_es}`} default={lang === "es"} />}
              </video>
            )}
            {caption(m) && <figcaption className="text-sm text-charcoal-500">{caption(m)}</figcaption>}
          </figure>
        ))}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setActive(null)}
          data-testid="nico-lightbox"
        >
          <button className="absolute top-4 right-4 text-white/90" aria-label="Close" data-testid="nico-lightbox-close"><X size={28} /></button>
          <figure className="max-h-[90vh] max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img src={`${BACKEND}${active.url}`} alt={alt(active)} className="max-h-[80vh] w-auto rounded-xl object-contain" />
            {caption(active) && <figcaption className="mt-2 text-center text-sm text-white/80">{caption(active)}</figcaption>}
          </figure>
        </div>
      )}
    </div>
  );
}
